import { WidgetType } from 'types/widget.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { responsiveShapeResize } from 'widgets/store';

import { Frame, OnResizeGroupEvent, OnResizeGroupStartEvent } from '../BoundingBox.types';
import {
  computeResponsiveGroupMinWidth,
  findShapeWidgetPadding,
  processGraphicWidgets,
  processInternalShapeWidgets,
  processTextWidgets,
  sortByTopPxInPlace,
} from './responsiveGroup.helpers';
import {
  BackgroundShape,
  FixedWidget,
  ResizeDirection,
  ResponsiveShapeCell,
  ResponsiveShapeOptions,
  ResponsiveTextWidget,
} from './responsiveGroup.types';

/**
 * Class to handle resizing a responsive group
 *
 * A responsive group contains:
 * - At least 1 background shape that encapsulates all text
 * - At least 1 text widget
 * - MAX 1 fixed widget (these are icons / images)
 * - Any number of internal shapes
 *
 * Limitations:
 * - Only supports a single fixed widget
 * - Doesn't support layouts with more than 2 columns
 * - Layouts with fixed widget only supports text on either side of the fixed widget
 *    - i.e. cannot have fixed widget in between 2 text widgets
 */
class ResponsiveGroup {
  // Background shape of the group - should always be defined
  private _backgroundShape: BackgroundShape = {
    shapeId: '',
    shapeEl: null,
    bottomPadding: 0,
    topPadding: 0,
  };

  private _originalWidthBeforeResize: number = 0;

  // Text widgets in the group - not including fixed text wrapped by 1:1 internal shape
  private _textWidgets: { [key: string]: ResponsiveTextWidget } = {};

  // Internal shapes
  private _shapeWidgets: { [key: string]: ResponsiveShapeCell } = {};

  // Fixed widgets - Only supports 1
  private _fixedWidgets: { [key: string]: FixedWidget } = {};

  // Resize direction - used to determine the positioning of fixed widgets
  private _resizeDirection: ResizeDirection = ResizeDirection.Right;

  // Options for the responsive group
  private _options: ResponsiveShapeOptions = { zoom: 1 };

  /**
   * Initialize responsive group from the provided textWidgets, shapeWidgets, and graphicWidgets arrays.
   *
   * Should be called before calling `resize()` function
   *
   * @param {HTMLElement[]} textWidgets - The array to populate with text widgets.
   * @param {HTMLElement[]} shapeWidgets - The array to populate with basic shape widgets.
   * @param {HTMLElement[]} graphicWidgets - The graphic elements in the group - note: limited to one graphic element
   */
  public init(
    widgetArrs: {
      textWidgets: HTMLElement[];
      shapeWidgets: HTMLElement[];
      graphicWidgets: HTMLElement[];
    },
    event: OnResizeGroupStartEvent,
    targetPosition: { top: number; left: number },
    options: ResponsiveShapeOptions,
  ) {
    this._options = options;

    const { textWidgets, shapeWidgets, graphicWidgets } = widgetArrs;
    const allTextWidgets = [...textWidgets];

    // Set the direction - will be used by resize event handler
    const direction = event.direction;
    this._resizeDirection = direction[0] === -1 ? ResizeDirection.Left : ResizeDirection.Right;

    const { left: groupLeftPx } = targetPosition;
    //@ts-ignore
    const moveableTarget = event.dragStart?.target;
    const groupWidthPx = moveableTarget?.clientWidth / options.zoom;
    this._originalWidthBeforeResize = groupWidthPx;

    // Find the fixed widgets - currently only supports a single fixed widget
    // All icons are fixed
    // Images fixed if they have an applied frame
    this._fixedWidgets = processGraphicWidgets(graphicWidgets, allTextWidgets, {
      leftPx: groupLeftPx,
      widthPx: groupWidthPx,
    });

    // Process the shape widgets to find wrapping shapes and the background shape
    const {
      bgShapeId,
      bgShapeEl: bgShapeWidgetEl,
      bgShapeSize,
      cellShapeWidgets,
    } = processInternalShapeWidgets(
      shapeWidgets,
      allTextWidgets,
      Object.values(this._fixedWidgets).map((w) => w.el),
      this._fixedWidgets,
    );

    this._shapeWidgets = cellShapeWidgets;

    // sort the widgets by their top position - highest position to lowest position
    const allWidgets = [...textWidgets, ...graphicWidgets, ...shapeWidgets];
    const sortedWidgetsByTop = sortByTopPxInPlace(allWidgets);
    const sortedTextWidgetsByTop = sortedWidgetsByTop.filter(
      (widget) => getWidgetTypeFromId(widget.id) === WidgetType.Text,
    );

    // Process the text widgets
    this._textWidgets = processTextWidgets(
      {
        all: sortedWidgetsByTop,
        text: sortedTextWidgetsByTop,
      },
      {
        leftPx: groupLeftPx,
        widthPx: groupWidthPx,
      },
      [...Object.keys(cellShapeWidgets), bgShapeId],
    );

    // Compute padding for bg shape
    let topPadding = 0;
    let bottomPadding = 0;
    if (bgShapeWidgetEl) {
      const { topPadding: bgTopPadding, bottomPadding: bgBottomPadding } = findShapeWidgetPadding(
        sortedTextWidgetsByTop,
        bgShapeSize,
      );
      topPadding = bgTopPadding;
      bottomPadding = bgBottomPadding;
    }

    this._backgroundShape = {
      shapeId: bgShapeId,
      shapeEl: bgShapeWidgetEl,
      bottomPadding,
      topPadding,
    };
  }

  /**
   * Handle resize and repositioning of the adjacent widgets to the fixed widget
   *
   * @param adjacentWidgets
   * @param frameMap
   * @param translate
   * @param boundingWidthPx
   */
  _processFixedAdjacentWidgets(
    adjacentWidgets: string[],
    frameMap: Frame,
    translate: [number, number],
    boundingWidthPx: number,
  ) {
    adjacentWidgets.forEach((adjWidgetId) => {
      const adjacentWidget = this._textWidgets[adjWidgetId];
      if (!adjacentWidget) return;

      const adjWidgetEl = adjacentWidget.el;
      const { paddingLeft, paddingRight, originalWidth, originalLeftPx } = adjacentWidget;

      // Update frame for future resize events + resizeEnd
      const frame = frameMap.get(adjWidgetEl);
      if (frame) {
        frame.translate = translate;
        adjWidgetEl.style.transform = `translate(${translate[0]}px, ${translate[1]}px)`;
      }

      // Compute new width - should maintain same padding from group bb
      let newWidth = 0;
      if (typeof paddingLeft === 'number' && typeof paddingRight === 'number') {
        // can't be negative
        newWidth = Math.max(boundingWidthPx - paddingLeft - paddingRight, 0);
        adjWidgetEl.style.width = `${newWidth}px`;
      }

      // Adjust the left position of the adjacent widget if resizing from the left
      // New position = original position - deltaWidth
      if (this._resizeDirection === ResizeDirection.Left) {
        const deltaWidth = newWidth - originalWidth;
        adjWidgetEl.style.left = `${originalLeftPx - deltaWidth}px`;
      }
    });
  }

  _computeNewTextWidgetHeight(
    textWidgetIds: string[],
    textBesideFixedMap: { [key: string]: boolean },
    shape: {
      el: HTMLElement;
      topPadding: number;
      bottomPadding: number;
      leftPadding: number;
      rightPadding: number;
    },
  ) {
    // Update the height of the shape to the height of all the text widgets its wrapping
    let allTextWidgetHeightPx = 0;
    let firstTextWidgetTopPx: number | null = null;
    let minLeftPx: number = Infinity;
    let maxRightPx: number = -Infinity;
    let isBesideFixed = false;
    const numTextWidgets = textWidgetIds.length;
    for (let i = 0; i < numTextWidgets; i++) {
      const textWidgetId = textWidgetIds[i];
      const textWidget = this._textWidgets[textWidgetId];
      const textWidgetEl = textWidget.el;

      if (textBesideFixedMap[textWidgetId]) {
        isBesideFixed = true;
      }

      const leftPx = parseFloat(textWidgetEl.style.left);
      const rightPx = leftPx + textWidgetEl.clientWidth;
      if (leftPx < minLeftPx) minLeftPx = leftPx;
      if (rightPx > maxRightPx) maxRightPx = rightPx;

      // skip text widgets that are completely overlapped by other text widgets
      if (textWidget.isOverlappingTextWidget && numTextWidgets > 1) continue;

      const textWidgetHeightPx = textWidgetEl.clientHeight;

      if (firstTextWidgetTopPx === null) {
        firstTextWidgetTopPx = parseFloat(textWidgetEl.style.top);
      }

      // the gap below to the next widget should be considered for the height
      const textWidgetBottomPadding = textWidget.paddingBottom;
      const isLast = i === textWidgetIds.length - 1;
      const additionalPadding = isLast ? 0 : textWidgetBottomPadding;
      allTextWidgetHeightPx += textWidgetHeightPx + additionalPadding;
    }

    const {
      el,
      topPadding: shapeTopPadding,
      bottomPadding: shapeBottomPadding,
      leftPadding: shapeLeftPadding,
      rightPadding: shapeRightPadding,
    } = shape;
    const newHeightPx = allTextWidgetHeightPx + shapeBottomPadding + shapeTopPadding;
    const widthPx = isBesideFixed
      ? maxRightPx + shapeRightPadding - (minLeftPx - shapeLeftPadding)
      : parseFloat(el.style.width);
    return {
      heightPx: newHeightPx,
      widthPx,
      allTextWidgetHeightPx,
      firstTextWidgetTopPx,
      minLeftPx,
      isBesideFixed,
    };
  }

  /**
   * Resize the responsive group based on the moveable OnResizeGroup event
   * Must be called after `init()`
   *
   * @param event
   * @param frameMap
   */
  public resize(event: OnResizeGroupEvent, frameMap: Frame) {
    // The boundingWidth from moveable is the width of the group bounding box not accounting for zoom
    const boundingWidthPx = event.boundingWidth / this._options.zoom;

    // Map of text widgets beside the fixed widget
    const textBesideFixedMap: { [key: string]: boolean } = {};
    const totalNumText = Object.keys(this._textWidgets).length;

    ///// 1) PROCESS FIXED WIDGETS
    // Fixed widget should not be resized - only the text widgets beside it should be resized
    // Note: Should only be one fixed widget
    const isLeftResize = this._resizeDirection === ResizeDirection.Left;
    for (let fixedWidgetId in this._fixedWidgets) {
      const { el, leftWidgets, rightWidgets, paddingLeftPct } = this._fixedWidgets[fixedWidgetId];

      // Case 1: single column - keep padding from left
      // single column - note: only works well for centred fixed widgets
      if (leftWidgets.length === 0 && rightWidgets.length === 0) {
        if (typeof paddingLeftPct !== 'number') continue;
        if (!this._originalWidthBeforeResize) continue;

        // Compute translate based on delta width and padding percentage from left group bounds
        const deltaWidthPx = boundingWidthPx - this._originalWidthBeforeResize;
        const multiplier = isLeftResize ? -1 : 1;

        // Shift the fixed widget by the delta width, update frame map
        const fixedWidgetFrame = frameMap.get(el);
        if (fixedWidgetFrame) {
          const newTranslateX = multiplier * deltaWidthPx * paddingLeftPct;
          fixedWidgetFrame.translate = [newTranslateX, 0];
          el.style.transform = `translate(${newTranslateX}px, 0px)`;
        }
        continue;
      }

      /// Case 2: 2 columns - resize + reposition adjacent text widgets
      // Compute the delta width of the first widget to determine the shift of the fixed widget
      let deltaWidth = 0;

      // Determine the order of widgets to process based on the resize direction
      const firstAdjacentWidgetsToProcess = isLeftResize ? rightWidgets : leftWidgets;
      const secondAdjacentWidgetsToProcess = isLeftResize ? leftWidgets : rightWidgets;

      // Compute the delta width to resize the text widgets beside the fixed widget
      this._processFixedAdjacentWidgets(firstAdjacentWidgetsToProcess, frameMap, [0, 0], boundingWidthPx);
      firstAdjacentWidgetsToProcess.forEach((adjWidgetId) => {
        textBesideFixedMap[adjWidgetId] = true;
        const leftWidget = this._textWidgets[adjWidgetId];
        if (!leftWidget) return;

        const originalWidth = leftWidget.originalWidth;
        const leftWidgetEl = leftWidget.el;

        // Compute the delta width to translate the fixed widget by
        deltaWidth = parseFloat(leftWidgetEl.style.width) - originalWidth;
      });

      // Process right widgets
      // Resize the text widgets beside the fixed widget
      this._processFixedAdjacentWidgets(secondAdjacentWidgetsToProcess, frameMap, [0, 0], boundingWidthPx);
      secondAdjacentWidgetsToProcess.forEach((adjWidgetId) => {
        textBesideFixedMap[adjWidgetId] = true;
      });

      // Shift the fixed widget by the delta width, update frame map
      const fixedWidgetFrame = frameMap.get(el);
      if (fixedWidgetFrame) {
        const translateX = isLeftResize ? -deltaWidth : deltaWidth;
        fixedWidgetFrame.translate = [translateX, 0];
        el.style.transform = `translate(${translateX}px, 0px)`;
      }
    }

    ///// 2) PROCESS TEXT WIDGETS
    // Change the top position of the widgets - depending on the widget above it
    const textWidgets = this._textWidgets;
    const textWidgetIds = Object.keys(textWidgets);
    for (let i = 0; i < textWidgetIds.length; i++) {
      const currentTextWidgetId = textWidgetIds[i];
      const currentTextWidget = textWidgets[currentTextWidgetId];
      if (!currentTextWidget) continue;

      const currentTextWidgetEl = currentTextWidget.el;
      if (!currentTextWidgetEl) continue;

      // The first text widget doesn't have a widget above it
      // Otherwise, check the gap between the current text widget and the widget above it
      if (i !== 0) {
        const widgetAboveId = textWidgetIds[i - 1];
        const widgetAbove = this._textWidgets[widgetAboveId];
        const widgetAbovePaddingBottom = widgetAbove.paddingBottom;
        if (widgetAbovePaddingBottom === 0) continue;

        const widgetAboveEl = widgetAbove.el;
        if (!widgetAboveEl) continue;

        const widgetAboveBottomPx = parseFloat(widgetAboveEl.style.top) + widgetAboveEl.clientHeight;
        const top = parseFloat(currentTextWidgetEl.style.top);

        // if the gap to the above widget is not the same
        // then change the top position of the text widget
        if (top - widgetAboveBottomPx !== widgetAbovePaddingBottom) {
          currentTextWidgetEl.style.top = `${widgetAboveBottomPx + widgetAbovePaddingBottom}px`;
        }
      }

      // Update non-text widgets below this one
      const currentTextWidgetWidgetsBelow = currentTextWidget.widgetsBelow;
      if (currentTextWidgetWidgetsBelow) {
        const currentTextWidgetElBottom = parseFloat(currentTextWidgetEl.style.top) + currentTextWidgetEl.clientHeight;
        for (const widgetId in currentTextWidgetWidgetsBelow) {
          const widgetBelow = currentTextWidgetWidgetsBelow[widgetId];
          if (!widgetBelow) continue;

          const { padding, el: widgetEl } = widgetBelow;
          if (!widgetEl) continue;

          // if the gap to the above widget is not the same
          // then change the top position of the text widget
          const top = parseFloat(widgetEl.style.top);
          if (top !== currentTextWidgetElBottom + padding) {
            const newTop = currentTextWidgetElBottom + padding;
            widgetEl.style.top = `${newTop}px`;
          }

          // Update frame map - change translateY to 0 since we're setting the correct `top`
          const frame = frameMap.get(widgetEl);
          if (frame) {
            const translate = frame.translate || [0, 0];
            translate[1] = 0;
            frame.translate = translate;

            widgetEl.style.transform = `translate(${translate[0]}px, ${translate[1]}px)`;
          }
        }
      }
    }

    // 3) PROCESS INTERNAL SHAPES
    // Resize the internal shapes to fit the text/fixed widgets they wrap
    for (let shapeId in this._shapeWidgets) {
      const shapeWidget = this._shapeWidgets[shapeId];
      const {
        bottomPadding: shapeBottomPadding,
        topPadding: shapeTopPadding,
        leftPadding: shapeLeftPadding,
        rightPadding: shapeRightPadding,
        textWidgets: textWidgetsWrappedByShape,
        shapeEl,
      } = shapeWidget;

      //// CASE 1: Shape wraps fixed widget
      // Shape wraps a fixed widget - note: only 1 fixed widget allowed per group
      for (const fixedWidgetId of shapeWidget.fixedWidgets || []) {
        const fixedWidget = this._fixedWidgets[fixedWidgetId];
        if (!fixedWidget) continue;

        const fixedWidgetEl = fixedWidget.el;
        const fixedWidgetFrame = frameMap.get(fixedWidgetEl);
        const fixedWidgetTranslate = fixedWidgetFrame?.translate || [0, 0];
        const leftPx = parseFloat(fixedWidgetEl.style.left) + fixedWidgetTranslate[0];
        const topPx = parseFloat(fixedWidgetEl.style.top) + fixedWidgetTranslate[1];

        const newLeftPx = leftPx - shapeLeftPadding;
        const newTopPx = topPx - shapeTopPadding;
        const newWidthPx = fixedWidgetEl.clientWidth + shapeLeftPadding + shapeRightPadding;
        const newHeightPx = fixedWidgetEl.clientHeight + shapeTopPadding + shapeBottomPadding;

        responsiveShapeResize.resize(shapeId, newWidthPx, newHeightPx, {
          isResponsiveGroupResize: false,
        });

        shapeEl.style.top = `${newTopPx}px`;
        shapeEl.style.left = `${newLeftPx}px`;
        shapeEl.style.width = `${newWidthPx}px`;
        shapeEl.style.height = `${newHeightPx}px`;
        const frame = frameMap.get(shapeEl);
        if (frame) {
          frame.translate = [0, 0];
          shapeEl.style.transform = 'translate(0px, 0px)';
        }
      }

      // Shape should only wrap 1 of text or fixed widgets - if already processed fixed, don't need to go further
      if (!!shapeWidget.fixedWidgets?.length) continue;

      //// Case 2: Shape wraps text widget
      const {
        isBesideFixed,
        firstTextWidgetTopPx,
        minLeftPx,
        allTextWidgetHeightPx,
        heightPx: newHeightPx,
        widthPx,
      } = this._computeNewTextWidgetHeight(textWidgetsWrappedByShape, textBesideFixedMap, {
        el: shapeEl,
        topPadding: shapeTopPadding,
        bottomPadding: shapeBottomPadding,
        leftPadding: shapeLeftPadding,
        rightPadding: shapeRightPadding,
      });

      const result = responsiveShapeResize.resize(shapeId, widthPx, newHeightPx, {
        // Edge case: all text widgets are wrapped by the shape
        isResponsiveGroupResize: textWidgetsWrappedByShape.length === totalNumText,
        contentHeightPx: allTextWidgetHeightPx,
      });

      // Non 1:1 shape
      if (!result) {
        shapeEl.style.height = `${newHeightPx}px`;

        // adjust top and left position of the shape - should be first text widget top - padding top of the shape
        shapeEl.style.top = `${firstTextWidgetTopPx! - shapeTopPadding}px`;
        const frame = frameMap.get(shapeEl);
        let translate = [...frame?.translate] || [0, 0];
        translate[1] = 0;

        // Text is beside fixed widget - adjust left position
        if (isBesideFixed) {
          shapeEl.style.left = `${minLeftPx - shapeLeftPadding}px`;
          shapeEl.style.width = `${widthPx}px`;
          translate[0] = 0;
        }

        // Update frame map
        if (frame) {
          frame.translate = translate;
          shapeEl.style.transform = `translate(${translate[0]}px, ${translate[1]}px)`;
        }
      } else {
        // 1:1 shape - adjust based on result of responsiveShapeResize
        const { widthPx, heightPx } = result;
        const originalHeightPx = parseFloat(shapeEl.style.height);
        const shapeTopPx = parseFloat(shapeEl.style.top);
        const shapeLeftPx = parseFloat(shapeEl.style.left);
        const newTopPx = shapeTopPx - (originalHeightPx - newHeightPx) / 2;
        const newLeftPx = shapeLeftPx;
        shapeEl.style.width = `${widthPx}px`;
        shapeEl.style.height = `${heightPx}px`;
        shapeEl.style.left = `${newLeftPx}px`;
        shapeEl.style.top = `${newTopPx}px`;
      }
    }

    // 4) PROCESS BACKGROUND SHAPE
    // Should expand/contract with all text
    const { shapeId, bottomPadding, topPadding, shapeEl } = this._backgroundShape;

    if (!shapeEl) return;

    const {
      heightPx: newHeightPx,
      widthPx,
      allTextWidgetHeightPx,
    } = this._computeNewTextWidgetHeight(
      textWidgetIds,
      {},
      {
        el: shapeEl,
        topPadding: topPadding,
        bottomPadding: bottomPadding,
        leftPadding: 0,
        rightPadding: 0,
      },
    );

    const result = responsiveShapeResize.resize(shapeId, widthPx, newHeightPx, {
      isResponsiveGroupResize: true,
      contentHeightPx: allTextWidgetHeightPx,
    });

    if (!result) {
      shapeEl.style.height = `${newHeightPx}px`;
    } else {
      const { widthPx, heightPx } = result;
      const originalHeightPx = parseFloat(shapeEl.style.height);
      const shapeTopPx = parseFloat(shapeEl.style.top);
      const shapeLeftPx = parseFloat(shapeEl.style.left);
      const newTopPx = shapeTopPx - (originalHeightPx - newHeightPx) / 2;
      const newLeftPx = shapeLeftPx;
      shapeEl.style.width = `${widthPx}px`;
      shapeEl.style.height = `${heightPx}px`;
      shapeEl.style.left = `${newLeftPx}px`;
      shapeEl.style.top = `${newTopPx}px`;
    }
  }

  /**
   * Compute the min width of the responsive group if any
   * Currently, only supports for 1:1 bg shapes
   * @returns
   */
  public computeMinWidth() {
    return computeResponsiveGroupMinWidth(
      {
        ...this._backgroundShape,
        textWidgets: this._textWidgets,
        shapeWidgets: this._shapeWidgets,
        fixedWidgets: this._fixedWidgets,
        isBackgroundShape: true,
        resizeDirection: this._resizeDirection,
      },
      this._options.zoom,
    );
  }

  /**
   * Reset the responsive group to its initial state
   */
  public reset() {
    this._backgroundShape = {
      shapeId: '',
      shapeEl: null,
      bottomPadding: 0,
      topPadding: 0,
    };
    this._originalWidthBeforeResize = 0;
    this._textWidgets = {};
    this._shapeWidgets = {};
    this._fixedWidgets = {};
    this._resizeDirection = ResizeDirection.Right;
    this._options = { zoom: 1 };
  }

  /**
   * Check if elId is fixed
   * @param elId
   * @returns
   */
  public isElementFixed(elId: string) {
    return !!this._fixedWidgets[elId];
  }

  /**
   * Check if elId acts as a bg
   * @param elId
   * @returns
   */
  public isBgShape(elId: string) {
    if (this._backgroundShape.shapeId === elId) return true;

    const shape = this._shapeWidgets[elId];
    if (shape) {
      const numText = shape.textWidgets.length;
      const totalNumText = Object.keys(this._textWidgets).length;
      return numText === totalNumText;
    }
  }
}

export default ResponsiveGroup;
